import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import localforage from 'localforage';
import { onlinePanelAtom } from '../../components/OnlinePanel/OnlinePanelState';
import { IOption } from '../../components/Forms/types';
import {
  ILastRideDistanceCumulative,
  IVehicle,
  IVehicleGdprDataAllowed,
  IVehicleGroup,
  IVehicles,
  IVehiclesWithData,
  IVehicleWithData,
  VehiclesBasicInfo,
} from '../../components/Vehicles/types';
import { localForageEffect } from './effects/localForageEffect';
import { vehiclesDataAtom } from './vehiclesData';
import { customersAtom, getCustomersWithVehicles, selectedCustomersAtom } from './customers';
import { VehicleSettingInputForm } from '../../components/VehiclesSettings/Form';

/** Alias for compatibility; use onlinePanelAtom. */
export { onlinePanelAtom as onlinePanelSyncAtom } from '../../components/OnlinePanel/OnlinePanelState';

export const getSelectedVehiclesFromIndexedDb = async (): Promise<number[]> => {
  const selectedVehicles = await localforage.getItem<number[]>('selectedVehicles');
  if (!selectedVehicles) {
    return [];
  }
  return selectedVehicles;
};

/** Initial value is []; hydrated from IndexedDB in JotaiEffects. */
export const selectedVehiclesAtom = atom<number[]>([]);
export const selectedVehicleIdsAtom = atom((get) => new Set(get(selectedVehiclesAtom)));
export const hasSelectedVehiclesAtom = atom((get) => get(selectedVehiclesAtom).length > 0);
export const isVehicleSelectedAtomFamily = atomFamily((vehicleId: number) =>
  atom((get) => get(selectedVehicleIdsAtom).has(vehicleId))
);
export const selectedVehiclesLocalForageEffectAtom = localForageEffect(
  selectedVehiclesAtom,
  'selectedVehicles',
  200
);

export const vehiclesAtom = atom<IVehicles>({});
export const vehiclesOptionsAtom = atom<IOption[]>([]);
export const vehiclesBasicInfoAtom = atom<VehiclesBasicInfo>({});
export const vehicleGroupsAtom = atom<IVehicleGroup[]>([]);
export const isVehicleGdprDataAllowedAtom = atom<IVehicleGdprDataAllowed>({});
export const selectedVehicleStatusAtom = atom<string>('');
export const lastRideDistanceCumulativeAtom = atom<ILastRideDistanceCumulative | null>(null);

export const selectedVehiclesWithBasicInfoAtom = atom<VehiclesBasicInfo>((get) => {
  const ids = get(selectedVehiclesAtom);
  const vehiclesList = get(vehiclesBasicInfoAtom);
  const selectedVehiclesWithBasicInfo: VehiclesBasicInfo = {};
  ids.forEach((vehicleId: number) => {
    const vehicle = vehiclesList[vehicleId];
    if (vehicle) {
      selectedVehiclesWithBasicInfo[vehicleId] = vehicle;
    }
  });
  return selectedVehiclesWithBasicInfo;
});

export const selectedVehiclesWithDataAtom = atom<IVehiclesWithData>((get) => {
  const ids = get(selectedVehiclesAtom);
  const vehiclesList = get(vehiclesAtom);
  const vehiclesDataList = get(vehiclesDataAtom);
  const onlinePanels = get(onlinePanelAtom);
  const vehiclesWithDataList: IVehiclesWithData = {};
  ids.forEach((vehicleId: number) => {
    const vehicle = vehiclesList[vehicleId];
    const vehicleData = vehiclesDataList[vehicleId];
    const onlinePanel = onlinePanels[vehicleId];

    if (
      vehicle &&
      vehicleData &&
      vehicleData.position &&
      vehicleData.position.latitude &&
      vehicleData.position.longitude
    ) {
      const newVehicleWithData: IVehicleWithData = {
        id: vehicle.id,
        name: vehicle.name,
        licenceNumber: vehicle.licenceNumber,
        vehicleType: vehicle.vehicleType,
        vehicleGroups: vehicle.vehicleGroups,
        customerId: vehicle.customerId,
        driver: vehicle.driver,
        showInNavigator: vehicle.showInNavigator,
        showInNavigatorGroups: vehicle.showInNavigatorGroups,
        status: vehicleData.status,
        position: vehicleData.position,
        drivingMode: vehicleData.drivingMode,
        lastRide: vehicleData.lastRide,
        onlinePanel,
      };
      vehiclesWithDataList[vehicleId] = newVehicleWithData;
    }
  });
  return vehiclesWithDataList;
});

export const vehicleWithDataAtomFamily = atomFamily((vehicleId: number | false) =>
  atom<IVehicleWithData | null>((get) => {
    if (!vehicleId) return null;
    const vehiclesList = get(vehiclesAtom);
    const vehiclesDataList = get(vehiclesDataAtom);
    const onlinePanels = get(onlinePanelAtom);
    const vehicle = vehiclesList[vehicleId];
    const vehicleData = vehiclesDataList[vehicleId];
    const onlinePanel = onlinePanels[vehicleId];

    if (!vehicle) {
      return null;
    }

    const withData: IVehicleWithData = {
      id: vehicle.id,
      name: vehicle.name,
      licenceNumber: vehicle.licenceNumber,
      vehicleType: vehicle.vehicleType,
      vehicleGroups: vehicle.vehicleGroups,
      customerId: vehicle.customerId,
      driver: vehicle.driver,
      showInNavigator: vehicle.showInNavigator,
      showInNavigatorGroups: vehicle.showInNavigatorGroups,
      status: vehicleData?.status,
      position: vehicleData?.position,
      drivingMode: vehicleData?.drivingMode,
      lastRide: vehicleData?.lastRide,
      onlinePanel,
    };
    return withData;
  })
);

export const vehicleByIdAtomFamily = atomFamily((vehicleId: number | false) =>
  atom<IVehicle | null>((get) => {
    if (!vehicleId) return null;
    const vehiclesList = get(vehiclesAtom);
    return vehiclesList[vehicleId] ?? null;
  })
);

function getSelectedGroupIdsFromVehicles(
  selectedVehicles: number[],
  vehicles: IVehicles,
  _vehicleGroups: IVehicleGroup[]
): Set<number> {
  const selectedGroupIds = new Set<number>();
  selectedVehicles.forEach((vehicleId) => {
    const vehicle = vehicles[vehicleId];
    if (!vehicle) return;
    const vGroups = vehicle.vehicleGroups;
    if (Array.isArray(vGroups)) {
      vGroups.forEach((g: unknown) => {
        if (typeof g === 'number') {
          selectedGroupIds.add(g);
        } else if (g && typeof g === 'object' && g !== null && 'id' in g) {
          selectedGroupIds.add((g as { id: number }).id);
        }
      });
    }
  });
  return selectedGroupIds;
}

export const vehicleGroupsBySelectedVehiclesAtom = atom<IVehicleGroup[]>((get) => {
  const ids = get(selectedVehiclesAtom);
  const vehicles = get(vehiclesAtom);
  const vehicleGroupsList = get(vehicleGroupsAtom);
  const selectedGroupIds = getSelectedGroupIdsFromVehicles(ids, vehicles, vehicleGroupsList);
  return vehicleGroupsList
    .filter((group) => selectedGroupIds.has(group.id))
    .sort((a, b) => a.id - b.id);
});

/** Vehicle groups filtered by currently selected customers (for Navigator sidebar). */
export const vehicleGroupsBySelectedCustomersAtom = atom<IVehicleGroup[]>((get) => {
  const vehicleGroups = get(vehicleGroupsAtom);
  const selectedIds = get(selectedCustomersAtom);
  return vehicleGroups.filter((g) => selectedIds.includes(g.customerId));
});

/** Customers that have at least one vehicle. Alias: customersWithVehiclesSelector. */
export const customersWithVehiclesAtom = atom((get) => {
  const customers = get(customersAtom);
  const vehicles = get(vehiclesAtom);
  return getCustomersWithVehicles(customers, vehicles);
});
export const customersWithVehiclesSelector = customersWithVehiclesAtom;

// Aliases for migration from Recoil (same API: selector/selectorFamily → atom/atomFamily)
export const selectedVehiclesWithBasicInfoSelector = selectedVehiclesWithBasicInfoAtom;
export const selectedVehiclesWithDataSelector = selectedVehiclesWithDataAtom;
export const vehicleWithDataSelector = vehicleWithDataAtomFamily;
export const vehicleByIdSelector = vehicleByIdAtomFamily;
export const vehicleGroupsBySelectedVehiclesSelector = vehicleGroupsBySelectedVehiclesAtom;

/** Reminder prefilled from the vehicle form; `vehicleId` is where the reminder form returns. */
export const reminderFromVehicleDetail = atom<{
  vehicleId: number;
  customerId?: number;
  name: string;
  value: string;
  message: string;
} | null>(null);

/**
 * Unsaved vehicle form values, kept while the user is away creating a reminder.
 * Cleared when the form is closed or saved.
 */
export const vehicleDetailData = atom<{
  vehicleId: number;
  raw: VehicleSettingInputForm | null;
  values: VehicleSettingInputForm;
} | null>(null);
